// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — the spherical geometry. This IS the game: the distance and bearing a
// guess reveals, and the P2P-sync invariant (every peer computes byte-identical
// clues from the same bundled centroids), all live here as pure functions.

export type LonLat = readonly [number, number];

/** Mean Earth radius (km). */
export const EARTH_R = 6371;

/** The farthest two points can be — antipodal, half the great circle. */
export const MAX_KM = Math.PI * EARTH_R; // ≈ 20015

/** Warmth falls off over this distance; tuned so a bordering country reads "hot"
 *  and the far side of the world reads "cold". Pinned by tests. */
export const HEAT_SCALE_KM = 2800;

const toRad = (d: number): number => (d * Math.PI) / 180;
const toDeg = (r: number): number => (r * 180) / Math.PI;

/** Great-circle distance between two [lon,lat] points, in kilometres. */
export function haversine(a: LonLat, b: LonLat): number {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLon / 2);
  const h = s1 * s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial great-circle bearing FROM a TO b, degrees clockwise from north (0..360). */
export function bearing(a: LonLat, b: LonLat): number {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Warmth 0..1 for a distance — 1 at the target, decaying smoothly. This is the
 * single "how close am I" number the map heat AND the proximity % both read, so
 * the colour and the percentage can never disagree.
 */
export function heat(km: number): number {
  return Math.exp(-Math.max(0, km) / HEAT_SCALE_KM);
}

/** Proximity percentage 0..100 shown to the player. 100 only means solved. */
export function proximityPct(km: number): number {
  return Math.round(100 * heat(km));
}

const ARROWS = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'] as const;

/** The 8-way arrow glyph nearest a bearing (fallback / a11y label). */
export function arrowChar(deg: number): string {
  const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
  return ARROWS[i];
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/** The 8-way compass label nearest a bearing, e.g. "NE". */
export function compass(deg: number): string {
  const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
  return COMPASS[i];
}

/** Equirectangular projection of [lon,lat] into a w×h box (top-left origin). */
export function project(lon: number, lat: number, w: number, h: number): [number, number] {
  return [((lon + 180) / 360) * w, ((90 - lat) / 180) * h];
}

/** A friendly distance string ("340 km" / "5,280 km"). */
export function formatKm(km: number): string {
  const r = Math.round(km);
  return `${r.toLocaleString('en-US')} km`;
}
